import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { RegistrationMethod, TrackedFailureReason } from '../constants';
import { VerificationTracker } from '../types';
import { VerifyUserForRegistration } from './__generated__/VerifyUserForRegistration';

export const VERIFY_USER_FOR_REGISTRATION = gql`
  mutation VerifyUserForRegistration($input: VerifyUserForRegistrationInput!) {
    verifyUserForRegistration(input: $input) {
      code
      success
    }
  }
`;

interface ClosedEmployeeIdVerificationProps {
  registrationMethod: RegistrationMethod.CLOSED_EMPLOYEE_ID;
  employeeId: string;
  email: string;
  organisationId: string;
}
interface OpenEmailVerificationProps {
  registrationMethod: RegistrationMethod.OPEN_EMAIL;
  email: string;
  organisationId: string;
}
export type UseVerifyUserForRegistrationProps =
  | ClosedEmployeeIdVerificationProps
  | OpenEmailVerificationProps;

interface UseVerifyUserForRegistration {
  tracker: VerificationTracker;
  onError(error: string): void;
  onSuccess(data: VerifyUserForRegistration): void;
}
export const useVerifyUserForRegistration = ({
  tracker,
  onError,
  onSuccess,
}: UseVerifyUserForRegistration) => {
  const { t: translate } = useTranslation('shared');
  const genericErrorMessage = translate('errors.messages.failed');

  const [verifyUserForRegistration, { loading }] =
    useMutation<VerifyUserForRegistration>(VERIFY_USER_FOR_REGISTRATION, {
      onCompleted: data => {
        if (data?.verifyUserForRegistration?.success) {
          tracker.attempted();
          onSuccess(data);
        } else {
          tracker.failed(TrackedFailureReason.Unknown);
          onError(genericErrorMessage);
        }
      },
      onError: error => {
        tracker.failed(error);
        onError(genericErrorMessage);
      },
    });

  const callVerifyUserForRegistration = async (
    userDetails: UseVerifyUserForRegistrationProps,
  ) =>
    verifyUserForRegistration({
      variables: {
        input: {
          registrationMethod: {
            type: userDetails.registrationMethod,
          },
          user: {
            email: userDetails.email,
            ...(userDetails.registrationMethod === 'CLOSED_EMPLOYEE_ID' && {
              identifiers: [
                {
                  key: 'EMPLOYEE_ID',
                  value: userDetails.employeeId,
                },
              ],
            }),
          },
          organisation: {
            id: userDetails.organisationId,
          },
        },
      },
    });

  return {
    callVerifyUserForRegistration,
    loading,
  };
};
